import { Card } from "../../components/ui/card";
import { FileText, AlertTriangle, CheckCircle, ArrowRight, Gavel } from "lucide-react";
import Leaders from "./Leaders";

export default function About() {
  return (
    <section id="about" className="py-20 bg-secondary/50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            About the Initiative
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Sangli Miraj Kupwad Mahanagarpalika has introduced a blockchain-powered infrastructure registry system to digitally track and manage all public works such as roads, gutters, and footpaths.      This system ensures that every project is geo-tagged, transparently recorded, and permanently stored, eliminating duplication, misuse of funds, and lack of accountability.

          </p>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mt-4">

            By leveraging blockchain technology, SMKMC aims to build a future-ready, transparent, and citizen-centric governance model.
          </p>
        </div>

        <Leaders />

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content Column */}
          <div className="space-y-8">
            {/* Auction Importance */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <Gavel className="w-6 h-6 text-primary" />
                <h3 className="text-xl font-semibold text-foreground">
                  Why Infrastructure Tracking Matters
                </h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                The SMKC Blockchain Infrastructure Registry System prevents duplicate road construction, ensures proper use of funds, and maintains a permanent record of all infrastructure work. It also supports better planning and auditing through secure, transparent, and real-time data.
              </p>
            </div>

            {/* Problems */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="w-6 h-6 text-destructive" />
                <h3 className="text-xl font-semibold text-foreground">
                  Current Challenges
                </h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-destructive rounded-full mt-2"></div>
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">Fraud & Forgery:</strong>
                    Paper-based documents vulnerable to manipulation

                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-destructive rounded-full mt-2"></div>
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">Lack of Transparency:</strong>
                    Limited visibility into document history
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-destructive rounded-full mt-2"></div>
                  <p className="text-muted-foreground">
                    <strong className="text-foreground">Trust Issues:</strong>
                    Difficulty in verification and authentication
                  </p>
                </div>
              </div>
            </div>

            {/* Solution */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h3 className="text-xl font-semibold text-foreground">
                  Blockchain
                </h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">

                Our blockchain-based system creates an <strong>immutable, decentralized, and transparent</strong>  ledger for all documents, ensuring complete traceability and eliminating fraud.


              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                <Card className="p-4 bg-green-50 border-green-200">
                  <div className="text-center">
                    <div className="w-8 h-8 bg-green-600 rounded-full mx-auto mb-2 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">✓</span>
                    </div>
                    <p className="text-sm font-medium text-green-800">Immutable</p>
                  </div>
                </Card>
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <div className="text-center">
                    <div className="w-8 h-8 bg-primary rounded-full mx-auto mb-2 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">⚡</span>
                    </div>
                    <p className="text-sm font-medium text-primary">Decentralized</p>
                  </div>
                </Card>
                <Card className="p-4 bg-orange-50 border-orange-200">
                  <div className="text-center">
                    <div className="w-8 h-8 bg-accent rounded-full mx-auto mb-2 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">👁</span>
                    </div>
                    <p className="text-sm font-medium text-orange-800">Transparent</p>
                  </div>
                </Card>
              </div>
            </div>
          </div>

          {/* Infographic Column */}
          <div className="relative">
            <Card className="p-8 bg-gradient-to-br from-white to-blue-50 border-primary/20">
              <h4 className="text-lg font-semibold text-center mb-8 text-foreground">
                Document Lifecycle on Blockchain
              </h4>

              <div className="space-y-6">
                {/* Step 1 */}
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center text-white font-bold">
                    1
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Project Data Entry</p>
                    <p className="text-sm text-muted-foreground">Officers enter infrastructure details</p>
                  </div>
                  <FileText className="w-6 h-6 text-primary" />
                </div>

                <div className="flex justify-center">
                  <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" />
                </div>

                {/* Step 2 */}
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Validation Process</p>
                    <p className="text-sm text-muted-foreground">Two-level approval ensures accuracy</p>
                  </div>
                  <Gavel className="w-6 h-6 text-accent" />
                </div>
                <div className="flex justify-center">
                  <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" />
                </div>

                {/* Step 3 */}
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold">
                    3
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Blockchain Recording</p>
                    <p className="text-sm text-muted-foreground">   Approved data is securely stored</p>
                  </div>
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>

                <div className="flex justify-center">
                  <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" />
                </div>

                {/* Step 4 */}
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                    4
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Public Access</p>
                    <p className="text-sm text-muted-foreground">   Citizens can verify all records</p>
                  </div>
                  <Gavel className="w-6 h-6 text-blue-600" />
                </div>

                <div className="flex justify-center">
                  <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" />
                </div>

                {/* Step 5 */}
                {/* <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center text-white font-bold">
                    5
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-foreground">Agreement & Allotment</p>
                    <p className="text-sm text-muted-foreground">Winner gets lease deed & certificate</p>
                  </div>
                  <FileText className="w-6 h-6 text-orange-600" />
                </div> */}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
